/**
 * Club role labels, tones, and persona groupings for admin People UI.
 * Mirrors `public.club_role` — keep in sync with packages/db/migrations.
 */

import type { ClubRole } from './index';

export const ALL_CLUB_ROLES: ClubRole[] = [
  'platform_owner',
  'president',
  'membership_officer',
  'secretary',
  'treasurer',
  'marketing',
  'events_officer',
  'committee_member',
  'head_coach',
  'squad_coach',
  'relief_coach',
  'junior_coach',
  'parent',
  'swimmer',
];

export const ROLE_LABEL: Record<ClubRole, string> = {
  platform_owner: 'Platform owner',
  president: 'President',
  membership_officer: 'Membership',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
  marketing: 'Marketing',
  events_officer: 'Events',
  committee_member: 'Committee member',
  head_coach: 'Head coach',
  squad_coach: 'Squad coach',
  relief_coach: 'Relief coach',
  junior_coach: 'Junior coach',
  parent: 'Parent',
  swimmer: 'Swimmer',
};

export const ROLE_TONE: Record<ClubRole, string> = {
  platform_owner: 'bg-violet-200 text-violet-950',
  president: 'bg-violet-100 text-violet-800',
  membership_officer: 'bg-slate-100 text-slate-700',
  secretary: 'bg-slate-100 text-slate-700',
  treasurer: 'bg-emerald-100 text-emerald-800',
  marketing: 'bg-pink-100 text-pink-800',
  events_officer: 'bg-amber-100 text-amber-800',
  committee_member: 'bg-purple-100 text-purple-800',
  head_coach: 'bg-sky-100 text-sky-800',
  squad_coach: 'bg-sky-50 text-sky-700',
  relief_coach: 'bg-sky-50 text-sky-600',
  junior_coach: 'bg-sky-50 text-sky-500',
  parent: 'bg-indigo-100 text-indigo-800',
  swimmer: 'bg-cyan-100 text-cyan-800',
};

export const COMMITTEE_ROLES: ClubRole[] = [
  'president',
  'membership_officer',
  'secretary',
  'treasurer',
  'marketing',
  'events_officer',
  // Generic seat. Last so a later named role wins primaryDisplayRole.
  'committee_member',
];

export const COACH_ROLES: ClubRole[] = [
  'head_coach',
  'squad_coach',
  'relief_coach',
  'junior_coach',
];

/**
 * Coaching-day notices (calendar reminders, published programs, check-ins)
 * go to every coach role. Junior coach is included so a later relief junior
 * coach gets the same notices as the coaches already on the roster.
 */
export function receivesCoachingDayNotices(roles: readonly string[] | null | undefined): boolean {
  return (roles ?? []).some((role) => (COACH_ROLES as readonly string[]).includes(role));
}

/**
 * Parent-app Coach tab. Every coach role qualifies, including assistant
 * coaches (relief and junior). A parent, swimmer, or committee role does not.
 * The old per-membership `pwa_coach_tools` flag is not an input.
 */
export function showsParentAppCoachTab(roles: readonly string[] | null | undefined): boolean {
  return receivesCoachingDayNotices(roles);
}

/** Priority order for `club_memberships.role` display field. */
const DISPLAY_ROLE_PRIORITY: ClubRole[] = [
  'platform_owner',
  ...COMMITTEE_ROLES,
  ...COACH_ROLES,
  'parent',
  'swimmer',
];

export type PeoplePersona =
  | 'all'
  | 'committee'
  | 'coaches'
  | 'parents'
  | 'swimmers'
  | 'families'
  | 'pipeline';

export const PEOPLE_PERSONA_LABEL: Record<PeoplePersona, string> = {
  all: 'Everyone',
  committee: 'Committee',
  coaches: 'Coaches',
  parents: 'Parents',
  swimmers: 'Swimmers',
  families: 'Families',
  pipeline: 'In pipeline',
};

export function primaryDisplayRole(roles: ClubRole[]): ClubRole | null {
  for (const role of DISPLAY_ROLE_PRIORITY) {
    if (roles.includes(role)) return role;
  }
  return null;
}

/** Row colour groups on admin People table — one category per member. */
export type PeopleRowCategory = 'committee' | 'coach' | 'parent' | 'swimmer' | 'neutral';

export const PEOPLE_ROW_CATEGORY_LABEL: Record<PeopleRowCategory, string> = {
  committee: 'Committee',
  coach: 'Coach',
  parent: 'Parent',
  swimmer: 'Swimmer',
  neutral: 'Other / pipeline',
};

/** Bold row palette — distinct hues per role; bg *-200 + *-950 text (~7:1+ contrast). */
export type PeopleRowTheme = {
  bg: string;
  text: string;
  muted: string;
  faint: string;
  cell: string;
  swatch: string;
};

export const PEOPLE_ROW_THEME: Record<PeopleRowCategory, PeopleRowTheme> = {
  committee: {
    bg: 'bg-purple-200',
    text: 'text-purple-950',
    muted: 'text-purple-900/80',
    faint: 'text-purple-800/65',
    cell: 'border-b border-purple-300/60',
    swatch: 'border-purple-500',
  },
  coach: {
    bg: 'bg-blue-200',
    text: 'text-blue-950',
    muted: 'text-blue-900/80',
    faint: 'text-blue-800/65',
    cell: 'border-b border-blue-300/60',
    swatch: 'border-blue-500',
  },
  parent: {
    bg: 'bg-amber-200',
    text: 'text-amber-950',
    muted: 'text-amber-900/80',
    faint: 'text-amber-800/65',
    cell: 'border-b border-amber-300/60',
    swatch: 'border-amber-500',
  },
  swimmer: {
    bg: 'bg-teal-200',
    text: 'text-teal-950',
    muted: 'text-teal-900/80',
    faint: 'text-teal-800/65',
    cell: 'border-b border-teal-300/60',
    swatch: 'border-teal-500',
  },
  neutral: {
    bg: 'bg-slate-200',
    text: 'text-slate-900',
    muted: 'text-slate-700',
    faint: 'text-slate-500',
    cell: 'border-b border-slate-300/60',
    swatch: 'border-slate-400',
  },
};

/** @deprecated Prefer {@link PEOPLE_ROW_THEME}.bg — kept for callers that only need background. */
export const PEOPLE_ROW_BG: Record<PeopleRowCategory, string> = {
  committee: PEOPLE_ROW_THEME.committee.bg,
  coach: PEOPLE_ROW_THEME.coach.bg,
  parent: PEOPLE_ROW_THEME.parent.bg,
  swimmer: PEOPLE_ROW_THEME.swimmer.bg,
  neutral: PEOPLE_ROW_THEME.neutral.bg,
};

export type PeoplePersonaPillStyle = {
  idle: string;
  active: string;
  countIdle: string;
  countActive: string;
};

function personaPillFromRowCategory(cat: PeopleRowCategory): PeoplePersonaPillStyle {
  const t = PEOPLE_ROW_THEME[cat];
  switch (cat) {
    case 'committee':
      return {
        idle: `${t.bg} ${t.text} border-0 shadow-sm hover:bg-purple-300 font-semibold`,
        active: 'border-0 bg-purple-700 text-white shadow-md ring-2 ring-purple-900/30 font-semibold',
        countIdle: 'text-purple-900/80',
        countActive: 'text-purple-200',
      };
    case 'coach':
      return {
        idle: `${t.bg} ${t.text} border-0 shadow-sm hover:bg-blue-300 font-semibold`,
        active: 'border-0 bg-blue-700 text-white shadow-md ring-2 ring-blue-900/30 font-semibold',
        countIdle: 'text-blue-900/80',
        countActive: 'text-blue-200',
      };
    case 'parent':
      return {
        idle: `${t.bg} ${t.text} border-0 shadow-sm hover:bg-amber-300 font-semibold`,
        active: 'border-0 bg-amber-700 text-white shadow-md ring-2 ring-amber-900/30 font-semibold',
        countIdle: 'text-amber-900/80',
        countActive: 'text-amber-100',
      };
    case 'swimmer':
      return {
        idle: `${t.bg} ${t.text} border-0 shadow-sm hover:bg-teal-300 font-semibold`,
        active: 'border-0 bg-teal-700 text-white shadow-md ring-2 ring-teal-900/30 font-semibold',
        countIdle: 'text-teal-900/80',
        countActive: 'text-teal-200',
      };
    case 'neutral':
      return {
        idle: `${t.bg} ${t.text} border-0 shadow-sm hover:bg-slate-300 font-semibold`,
        active: 'border-0 bg-slate-700 text-white shadow-md ring-2 ring-slate-900/30 font-semibold',
        countIdle: 'text-slate-700',
        countActive: 'text-slate-300',
      };
  }
}

const NEUTRAL_PERSONA_PILL: PeoplePersonaPillStyle = {
  idle: 'border-0 bg-slate-100 text-slate-800 shadow-sm hover:bg-slate-200 font-medium',
  active: 'border-0 bg-slate-800 text-white shadow-md ring-2 ring-slate-600/40 font-semibold',
  countIdle: 'text-slate-500',
  countActive: 'text-slate-300',
};

/** Persona filter pills — colours match {@link PEOPLE_ROW_BG} legend. */
export const PEOPLE_PERSONA_PILL: Record<PeoplePersona, PeoplePersonaPillStyle> = {
  all: NEUTRAL_PERSONA_PILL,
  families: NEUTRAL_PERSONA_PILL,
  committee: personaPillFromRowCategory('committee'),
  coaches: personaPillFromRowCategory('coach'),
  parents: personaPillFromRowCategory('parent'),
  swimmers: personaPillFromRowCategory('swimmer'),
  pipeline: personaPillFromRowCategory('neutral'),
};

/** Merge `user_roles` with legacy `club_memberships.role` for filters and row colour. */
export function normaliseMemberRoles(
  roles: readonly ClubRole[] | undefined | null,
  legacyRole?: string | null,
): ClubRole[] {
  const out = new Set<ClubRole>(roles ?? []);
  if (legacyRole && (ALL_CLUB_ROLES as string[]).includes(legacyRole)) {
    out.add(legacyRole as ClubRole);
  }
  return [...out];
}

/**
 * Pick one row colour when a member holds multiple roles.
 * Precedence: Committee → Coach → Parent → Swimmer → neutral (no role / pipeline-only).
 */
export function resolvePeopleRowCategory(
  roles: ClubRole[],
  opts?: { hasSwimmerProfile?: boolean },
): PeopleRowCategory {
  if (roles.includes('platform_owner') || roles.some((r) => COMMITTEE_ROLES.includes(r))) {
    return 'committee';
  }
  if (roles.some((r) => COACH_ROLES.includes(r))) return 'coach';
  if (roles.includes('parent')) return 'parent';
  if (roles.includes('swimmer') || opts?.hasSwimmerProfile) return 'swimmer';
  return 'neutral';
}

export function peopleRowBgClass(
  roles: ClubRole[],
  opts?: { hasSwimmerProfile?: boolean },
): string {
  return PEOPLE_ROW_THEME[resolvePeopleRowCategory(roles, opts)].bg;
}

export function peopleRowThemeClass(
  roles: ClubRole[],
  opts?: { hasSwimmerProfile?: boolean },
): PeopleRowTheme {
  return PEOPLE_ROW_THEME[resolvePeopleRowCategory(roles, opts)];
}

/** Parent is the guardian role. A swimmer row alone is not this role. */
export function holdsParentRole(roles: readonly ClubRole[]): boolean {
  return roles.includes('parent');
}

export function holdsSwimmerRole(roles: readonly ClubRole[]): boolean {
  return roles.includes('swimmer');
}

/**
 * Swimmers filter: swimmer role, and not the parent/guardian role.
 * A swimmer profile without the swimmer role does not qualify.
 * Holding both roles is a data error — excluded here and listed for a hand fix.
 */
export function matchesSwimmersFilter(roles: readonly ClubRole[]): boolean {
  return holdsSwimmerRole(roles) && !holdsParentRole(roles);
}

/** Same person holds swimmer and parent. Read-only signal; never auto-corrected. */
export function isDualSwimmerParent(roles: readonly ClubRole[]): boolean {
  return holdsSwimmerRole(roles) && holdsParentRole(roles);
}

export type PersonaMemberInput = {
  roles?: readonly ClubRole[] | null;
  role?: string | null;
  induction_status?: string | null;
  inductionStatus?: string | null;
  swimmer?: unknown;
  hasSwimmerProfile?: boolean;
};

export function personaSubjectFromMember(member: PersonaMemberInput): {
  roles: ClubRole[];
  inductionStatus: string;
  hasSwimmerProfile: boolean;
} {
  return {
    roles: normaliseMemberRoles(member.roles, member.role),
    inductionStatus: member.inductionStatus ?? member.induction_status ?? '',
    hasSwimmerProfile: member.hasSwimmerProfile ?? member.swimmer != null,
  };
}

export function memberMatchesPersona(persona: PeoplePersona, member: PersonaMemberInput): boolean {
  return matchesPersona(persona, personaSubjectFromMember(member));
}

export function memberIsDualSwimmerParent(member: PersonaMemberInput): boolean {
  return isDualSwimmerParent(personaSubjectFromMember(member).roles);
}

const PERSONA_COUNT_KEYS = ['committee', 'coaches', 'parents', 'swimmers', 'pipeline'] as const;

/** Role-tab counts. `families` stays 0 — the UI fills that from guardian links. */
export function countPersonas(members: readonly PersonaMemberInput[]): Record<PeoplePersona, number> {
  const out: Record<PeoplePersona, number> = {
    all: members.length,
    families: 0,
    committee: 0,
    coaches: 0,
    parents: 0,
    swimmers: 0,
    pipeline: 0,
  };
  for (const member of members) {
    const subject = personaSubjectFromMember(member);
    for (const persona of PERSONA_COUNT_KEYS) {
      if (matchesPersona(persona, subject)) out[persona] += 1;
    }
  }
  return out;
}

export const PEOPLE_FILTER_PERSONAS = ['committee', 'coaches', 'parents', 'swimmers', 'pipeline'] as const;
export type PeopleFilterPersona = (typeof PEOPLE_FILTER_PERSONAS)[number];

export function isPeopleFilterPersona(value: string | null | undefined): value is PeopleFilterPersona {
  return !!value && (PEOPLE_FILTER_PERSONAS as readonly string[]).includes(value);
}

export function matchesPersona(
  persona: PeoplePersona,
  opts: {
    roles: ClubRole[];
    inductionStatus: string;
    hasSwimmerProfile: boolean;
  },
): boolean {
  if (persona === 'all') return true;
  const { roles, inductionStatus } = opts;
  switch (persona) {
    case 'committee':
      return roles.some((r) => COMMITTEE_ROLES.includes(r));
    case 'coaches':
      return roles.some((r) => COACH_ROLES.includes(r));
    case 'parents':
      return holdsParentRole(roles);
    case 'swimmers':
      return matchesSwimmersFilter(roles);
    case 'pipeline':
      return inductionStatus !== 'active' && inductionStatus !== 'archived';
    default:
      return true;
  }
}
